import { useEffect, useMemo } from 'react';

/**
 * The stopwatch for one guest's game.
 *
 * It runs only while the game is on screen: from the moment the guest opens their station, across
 * every TRY AGAIN, until WELL DONE. Closing the station, locking the phone or switching apps
 * stops it, and opening the station again carries on from where it stood. Mistakes add up the
 * same way, so neither a fresh round nor a closed screen wipes the slate.
 *
 * It is kept in the phone's own storage under the voyage, era, station and seat, so a reload in
 * the middle of a game loses at most a second of it.
 */

export interface RunTally {
  ms: number;
  mistakes: number;
}

export interface ClockStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

/** A slice longer than this means the page was not really running (a sleeping phone), so it counts as this. */
const SLICE_CAP_MS = 5_000;
/** How often the running time is banked. */
const CHECKPOINT_MS = 1_000;

export class GameClock {
  private tally: RunTally = { ms: 0, mistakes: 0 };
  private since: number | null = null;

  constructor(
    private readonly key: string,
    private readonly store: ClockStore | null,
  ) {
    try {
      const saved = JSON.parse(store?.get(key) ?? 'null') as Partial<RunTally> | null;
      if (saved && Number.isFinite(saved.ms) && Number.isFinite(saved.mistakes)) {
        this.tally = { ms: Math.max(0, saved.ms ?? 0), mistakes: Math.max(0, saved.mistakes ?? 0) };
      }
    } catch {
      /* nothing saved, or nothing readable: start from zero */
    }
  }

  /** The game is on screen. */
  resume(now: number): void {
    if (this.since === null) this.since = now;
  }

  /** The game went off screen. */
  pause(now: number): void {
    this.bank(now);
    this.since = null;
  }

  /** Put the running time in the bank, and keep running. */
  checkpoint(now: number): void {
    if (this.since === null) return;
    this.bank(now);
    this.since = now;
  }

  mistake(): void {
    this.tally = { ...this.tally, mistakes: this.tally.mistakes + 1 };
    this.save();
  }

  read(now: number): RunTally {
    const running = this.since === null ? 0 : Math.min(SLICE_CAP_MS, Math.max(0, now - this.since));
    return { ms: Math.round(this.tally.ms + running), mistakes: this.tally.mistakes };
  }

  private bank(now: number): void {
    if (this.since === null) return;
    this.tally = { ...this.tally, ms: this.tally.ms + Math.min(SLICE_CAP_MS, Math.max(0, now - this.since)) };
    this.save();
  }

  private save(): void {
    try {
      this.store?.set(this.key, JSON.stringify(this.tally));
    } catch {
      /* private browsing: it still counts, it just will not survive a reload */
    }
  }
}

function browserStore(): ClockStore | null {
  try {
    const ls = window.localStorage;
    return { get: (key) => ls.getItem(key), set: (key, value) => ls.setItem(key, value) };
  } catch {
    return null;
  }
}

/** The storage key for one guest's game tonight. */
export function clockKey(voyageId: string, chapter: number, index: number, seat: number): string {
  return `continuum.run.${voyageId}.${chapter}.${index}.${seat}`;
}

/**
 * A clock that runs while this component is mounted and the page is visible. `key` null (someone
 * else's game, or one already finished) gives no clock at all. `paused` stops it while a meeting
 * covers the game: the table is voting, and nobody should lose points for that.
 */
export function useGameClock(key: string | null, paused = false): GameClock | null {
  const clock = useMemo(() => (key ? new GameClock(key, browserStore()) : null), [key]);
  useEffect(() => {
    if (!clock) return;
    const sync = () => {
      if (document.visibilityState === 'visible' && !paused) clock.resume(Date.now());
      else clock.pause(Date.now());
    };
    const hide = () => clock.pause(Date.now());
    sync();
    const id = setInterval(() => clock.checkpoint(Date.now()), CHECKPOINT_MS);
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('pagehide', hide);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('pagehide', hide);
      clock.pause(Date.now());
    };
  }, [clock, paused]);
  return clock;
}
